import { Injectable, Inject } from '@angular/core';
import { IpAddressService } from './ip-address.service';
import { ActionParams, NewRelicService } from './new-relic.service';
import { PARAM_TRANSFORMERS, LoggerParamsTransformer } from '../models';
import { LoggerConfig, LogLevel } from './logger-config';
import { LoggingEnvironment, LoggingEnvironmentConfig } from '../logging-environment.provider';
import {
  MegaBeaconService,
  eventTypeEnum,
  actionEnum,
  AdditionalDataParams,
  Response
} from '../logger/mega-beacon.service';
import { Observable } from 'rxjs';

export type PrefixStrategy = 'none' | 'camelCase' | 'underscore';

/**
 * Service to manage logging, both locally (to console) and to a server
 *
 * @export
 * @class LoggerService
 * @implements {OnDestroy}
 */
@Injectable()
export class LoggerService {
  /**
   * Use this to tag all log messages from this instance with the calling component/service/class
   *
   * @memberof LoggerService
   */
  public caller = '';

  /**
   * Creates an instance of LoggerService.
   * @param {NewRelicService} newRelicService Injected
   * @memberof LoggerService
   */
  constructor(
    private newRelicService: NewRelicService,
    @Inject(PARAM_TRANSFORMERS) private paramTransformers: LoggerParamsTransformer[],
    private ipService: IpAddressService,
    @Inject(LoggingEnvironment) private environment: LoggingEnvironmentConfig,
    private megaBeaconService: MegaBeaconService
  ) {}

  /**
   * Logs the message and parameters to console (if level is at or above the set Local Logging Level)
   *
   * @protected
   * @param {LogLevel} level
   * @param {string} msg
   * @param {ActionParams} params
   * @returns
   * @memberof LoggerService
   */
  protected localLog(level: LogLevel, msg: string, params: ActionParams) {
    if (level < LoggerConfig.LocalLogLevel) {
      return;
    }
    const funcName = LogLevel[level].toLowerCase();
    const logParams = [];
    if (this.caller) {
      logParams.push(this.caller);
    }
    if (msg) {
      logParams.push(msg);
    }
    if (params) {
      logParams.push(params);
    }
    console[funcName](...logParams);
  }

  /**
   * Logs the message and parameters to teh server (New Relic) if level is at or above the set Local Logging Level
   *
   * @protected
   * @param {LogLevel} level
   * @param {string} msg
   * @param {ActionParams} params
   * @returns {void}
   * @memberof LoggerService
   */
  protected serverLog(level: LogLevel, msg: string, params: ActionParams): void {
    if (level < LoggerConfig.ServerLogLevel) {
      return;
    }
    this.newRelicService.log(msg, this.caller, params);
  }

  get appVersion(): string {
    return this.environment.version ? this.environment.version : '';
  }

  /**
   * Updates a generic set of parameters to include otehr details, like who is logged in and the current time
   *
   * @protected
   * @param {{}} [params={}]
   * @returns {ActionParams}
   * @memberof LoggerService
   */
  protected updateParams(params: {} = {}): ActionParams {
    let updatedParams: ActionParams = params;
    updatedParams.timeStamp = Date.now();
    if (this.environment.version) {
      updatedParams.version = this.environment.version;
    }

    if (window.performance) {
      updatedParams.timeOffset = Math.round(window.performance.now());
    }
    updatedParams.ipAddress = this.ipService.ipAddress;
    this.paramTransformers
      .filter(transformer => transformer.initialized)
      .forEach(transformer => {
        updatedParams = {
          ...updatedParams,
          ...transformer.getParams()
        };
      });
    return updatedParams;
  }

  /**
   * Adds a message to the Log at the given Logging Level
   *
   * @param {LogLevel} level
   * @param {string} msg Message to log
   * @param {{}} params extra data to be logged (key/value pairs)
   * @param {boolean} [skipLocal=false] Option to only log to server
   * @memberof LoggerService
   */
  public doLog(level: LogLevel, msg: string, params: {}, skipLocal = false) {
    const updatedParams = this.updateParams(params);
    if (!skipLocal) {
      this.localLog(level, msg, updatedParams);
    }
    this.serverLog(level, msg, updatedParams);
  }

  public doLogMegaBeacon(
    eventType: eventTypeEnum,
    action: actionEnum,
    jsonSchemaFileName?: string,
    additionalData?: AdditionalDataParams,
    defaultApplicationWhenEmpty: string = ''
  ): Observable<Response> {
    const params: {} = {};
    const updatedParams = this.updateParams(params);
    if (!updatedParams.activeApplication) {
      updatedParams.activeApplication = defaultApplicationWhenEmpty;
    }

    return this.megaBeaconService
      .log(eventType, action, updatedParams, jsonSchemaFileName, additionalData)
      .pipe(response => response);
  }

  /**
   * Shortcut to log at LogLevel.LOG
   */
  public log(msg: string, params: {} = {}): void {
    this.doLog(LogLevel.LOG, msg, params);
  }

  /**
   * Shortcut to log at LogLevel.DEBUG
   */
  public debug(msg: string, params: {} = {}): void {
    this.doLog(LogLevel.DEBUG, msg, params);
  }

  /**
   * Shortcut to log at LogLevel.INFO
   */
  public info(msg: string, params: {} = {}): void {
    this.doLog(LogLevel.INFO, msg, params);
  }

  /**
   * Shortcut to log at LogLevel.WARN
   */
  public warn(msg: string, params: {} = {}): void {
    this.doLog(LogLevel.WARN, msg, params);
  }

  /**
   * Shortcut to log at LogLevel.ERROR
   */
  public error(msg: string, params: {} = {}): void {
    this.doLog(LogLevel.ERROR, msg, params);
  }

  /**
   * Shortcut to log at LogLevel.ERROR
   */
  public trace(msg: string, params: {} = {}): void {
    this.doLog(LogLevel.TRACE, msg, params);
  }

  /**
   * Gets a new instance of the logger, so the "caller" property is unique to that logger
   *
   * @param {string} caller
   * @returns {LoggerService}
   * @memberof LoggerService
   */
  public instance(caller: string): LoggerService {
    const loggerInstance = new LoggerService(
      this.newRelicService,
      this.paramTransformers,
      this.ipService,
      this.environment,
      this.megaBeaconService
    );
    loggerInstance.caller = caller;
    return loggerInstance;
  }

  /**
   * In order to work with parameters in New Relic (which does not support objects) this will
   * add a prefix to all properties of the given object
   *
   * @example
   * const logger = loggerService.instance('EntityLogger');
   * const entity = {id: '1234', name: 'my_entity', date: '2019/04/08'};
   * // Will create a log with additional properties "entityId", "entityName", "entityDate"
   * logger.log('Created Entity', {...logger.prefixProperties(entity, 'entity')});
   *
   * @param {{ [key: string]: any }} obj
   * @param {string} prefix
   * @param {PrefixStrategy} [prefixStrategy='camelCase']
   * @returns {{ [key: string]: any }}
   * @memberof LoggerService
   */
  public prefixProperties(
    obj: { [key: string]: any },
    prefix: string,
    prefixStrategy: PrefixStrategy = 'camelCase'
  ): { [key: string]: any } {
    if (prefixStrategy === 'underscore') {
      prefix += '_';
    }

    const prefixed = {};
    Object.keys(obj).forEach(key => {
      const newKey =
        prefix +
        (prefixStrategy !== 'camelCase' ? key : key.charAt(0).toUpperCase() + key.slice(1));
      prefixed[newKey] = obj[key];
    });
    return prefixed;
  }
}
